import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableCell,
  TableRow,
  WidthType,
  ImageRun,
  AlignmentType,
} from "docx";
import { saveAs } from "file-saver";
import moment from "moment";
import * as XLSX from "xlsx";
import { IMAGE_LOGO_BASE64 } from "./siriusLogoBase64";

let Project = "Forom";

/**
 * Export data to CSV file with fixed headers
 * @param {Array<Object>} rows - data to export
 * @param {string} fileName - base file name
 */
export const exportCSV = (rows, fileName = "export") => {
  const headers = [
    "Reference ID",
    "Amount",
    "Currency",
    "Recipient ID",
    "Email",
    "Memo",
    "Tags",
    "Taxable",
    "US Tax Activity",
    "Cover Fees",
  ];

  const separator = ";";

  const escapeValue = (value) => {
    if (value === null || value === undefined) return "";
    return `"${String(value).replace(/"/g, '""')}"`;
  };

  const csvLines = [
    headers.join(separator),

    ...rows.map((row) =>
      [
        row["Reference ID"],
        row["Amount"],
        row["Currency"],
        row["Recipient ID"],
        row["Email"],
        row["Memo"],
        row["Tags"] ?? "",
        row["Taxable"],
        row["US Tax Activity"],
        row["Cover Fees"],
      ]
        .map(escapeValue)
        .join(separator)
    ),
  ];

  const csvContent = csvLines.join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `${fileName}_${Date.now()}.csv`;
  link.click();

  URL.revokeObjectURL(url);
};

/**
 * @function getDoc
 * @param {string} months - month or range of months displayed in the document header
 * @param {...Table|Paragraph} tablesByRole - tables and headers generated per project role
 * @description - builds and returns a Word document containing a monthly customer time report with multiple tables grouped by role
 * @returns {Document} - generated Word document instance
 */
let getDoc = (months, ...tablesByRole) => {
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({
            children: [
              new ImageRun({
                data: IMAGE_LOGO_BASE64,
                transformation: {
                  width: 100,
                  height: 100,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 200 },
          }),
          new Paragraph({
            text: "Monthly customer time report for - " + Project,
            heading: HeadingLevel.HEADING_1,
          }),
          new Paragraph({
            text: "Month - " + months,
            heading: HeadingLevel.HEADING_7,
            spacing: { before: 200 },
          }),
          new Paragraph({
            text: "Sirius Invoice reference – INV -",
            heading: HeadingLevel.HEADING_5,
            spacing: { before: 200, after: 300 },
          }),
          ...tablesByRole.flatMap((table) => [
            table,
            new Paragraph({ text: " " }),
          ]),
          new Paragraph({
            children: [
              new TextRun({
                text: "Notes",
                bold: true,
                underline: true,
              }),
            ],
            spacing: { before: 200 },
          }),
        ],
      },
    ],
  });

  return doc;
};

/**
 * @function getWeeksDate
 * @param {string|Date} fromDate
 * @param {string|Date} toDate
 */
let getWeeksDate = (fromDate, toDate) => {
  let weekData = {
    dates: [],
    weekNumber: [],
  };

  let startDate = moment(fromDate);

  while (moment(startDate) <= moment(toDate)) {
    let temp = moment(startDate).endOf("isoWeek");

    if (moment(temp) > moment(toDate)) {
      temp = moment(toDate);
    }

    weekData.dates.push(
      `${startDate.format("DD/MM")} - ${temp.format("DD/MM")}`
    );

    weekData.weekNumber.push(startDate.isoWeek());

    startDate = moment(temp).add(1, "days");
  }

  return weekData;
};

/**
 * @function newTableCell
 * @param {string} text - cell text content
 * @param {number} cellWidth - cell width in DXA units
 * @returns {TableCell} - word table cell
 */
let newTableCell = (text, cellWidth) => {
  return new TableCell({
    width: {
      size: cellWidth,
      type: WidthType.DXA,
    },
    children: [new Paragraph(text)],
  });
};

/**
 * @function customRound
 * @param {number} value - number to be rounded
 * @description - rounds the value
 * @returns {number} - rounded value
 */
function customRound(value) {
  const decimal = value % 1;
  return decimal > 0.4 ? Math.ceil(value) : Math.floor(value);
}

/**
 * @async
 * @function expFile
 * @param {Object} data - payroll and project data
 * @param {string} data.name - project name
 * @param {string|Date} data.fromDate - report start date
 * @param {string|Date} data.toDate - report end date
 * @param {Array<Object>} data.payroll - payroll entries
 * @description - generates and exports a DOCX report containing payroll data grouped by project roles and weeks
 */
export const expFile = async (data) => {
  Project = data.name;

  let weekConst = getWeeksDate(data.fromDate, data.toDate);

  const groupedByRole = {};

  for (const x of data.payroll) {
    const role = x.projectRole || "No Role";

    if (!groupedByRole[role]) groupedByRole[role] = [];

    groupedByRole[role].push(x);
  }

  const tablesByRole = [];

  for (const role in groupedByRole) {
    const userData = {};
    const users = groupedByRole[role];

    for (const x of users) {
      if (!userData[x.id]) {
        userData[x.id] = {
          hours: {},
          name: x.name,
        };
      }

      userData[x.id].hours[x.week] = customRound(
        parseFloat(x.payroll)
      ).toFixed(2);
    }

    const cellWidth = 9010 / (weekConst.weekNumber.length + 1);

    const columnWidths = Array(weekConst.weekNumber.length + 1).fill(
      cellWidth
    );

    const rows = [];

    const heading = [newTableCell("", cellWidth)];
    const afterHeader = [newTableCell("", cellWidth)];

    for (const date of weekConst.dates) {
      heading.push(newTableCell(date, cellWidth));
      afterHeader.push(newTableCell("", cellWidth));
    }

    rows.push(new TableRow({ children: heading }));
    rows.push(new TableRow({ children: afterHeader }));

    const totalHours = Array(weekConst.weekNumber.length).fill(0);

    for (const uid in userData) {
      const userRow = [newTableCell(userData[uid].name, cellWidth)];

      for (let j = 0; j < weekConst.weekNumber.length; j++) {
        const z = userData[uid].hours[weekConst.weekNumber[j]] || "";

        userRow.push(newTableCell(z, cellWidth));

        totalHours[j] += z !== "" ? parseFloat(z) : 0;
      }

      rows.push(new TableRow({ children: userRow }));
    }

    const totalRow = [
      new TableCell({
        width: { size: cellWidth, type: WidthType.DXA },
        children: [
          new Paragraph({
            text: `${role} Total`,
            bold: true,
          }),
        ],
      }),
    ];

    for (const t of totalHours) {
      totalRow.push(
        new TableCell({
          width: { size: cellWidth, type: WidthType.DXA },
          children: [
            new Paragraph({
              text: t.toFixed(2),
              bold: true,
            }),
          ],
        })
      );
    }

    rows.push(new TableRow({ children: totalRow }));

    const table = new Table({
      columnWidths,
      rows,
    });

    const roleHeader = new Paragraph({
      text: `${role} hours`,
      heading: HeadingLevel.HEADING_3,
      spacing: { after: 100 },
    });

    tablesByRole.push(roleHeader, table);
  }

  let months = moment(data.fromDate).format("MMMM");

  if (
    moment(data.toDate).month() !== moment(data.fromDate).month()
  ) {
    months += "/" + moment(data.toDate).format("MMMM");
  }

  Packer.toBlob(getDoc(months, ...tablesByRole)).then((blob) => {
    saveAs(
      blob,
      moment(data.fromDate).format("DD/MM") +
        " - " +
        moment(data.toDate).format("DD/MM") +
        " -- " +
        data.name +
        " Report.docx"
    );
  });
};

/**
 * SUMMARY TAB
 */
export function convertHierarchicalToCSV(data) {
  const rows = [];

  rows.push([
    "Employee",
    "Week",
    "Day",
    "Productive",
    "Idle",
    "Manual",
    "Rejected",
    "Overtime",
    "Payroll",
  ]);

  data.forEach((employee) => {
    const employeeTotal = calculateEmployeeTotal(employee);

    rows.push([
      `${employee.name} (Total: ${employeeTotal.toFixed(2)})`,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);

    rows.push(["", "", "", "", "", "", "", "", ""]);

    employee.weeks?.forEach((week) => {
      const weekTotal = calculateWeekTotal(week);

      rows.push([
        "",
        `Week ${week.weekNumber}`,
        "",
        "",
        "",
        "",
        "",
        "",
        `${weekTotal.toFixed(2)}`,
      ]);

      week.children?.forEach((day) => {
        rows.push([
          "",
          "",
          safeDate(day.date),
          formatNum(day.activeTime),
          formatNum(day.idleTime),
          formatNum(day.requested),
          formatNum(day.rejectedTime),
          formatNum(day.overtime),
          formatNum(day.payroll),
        ]);
      });

      // 1 line between weeks
      rows.push(["", "", "", "", "", "", "", "", ""]);
    });

    // 2 lines between users
    rows.push(["", "", "", "", "", "", "", "", ""]);
    rows.push(["", "", "", "", "", "", "", "", ""]);
  });

  return rows;
}

/**
 * DAILY DETAILS TAB
 */
export function convertDailyDetails(data) {
  const rows = [];

  rows.push([
    "ID",
    "Name",
    "Email",
    "Week",
    "Day",
    "Productive",
    "Idle",
    "Manual",
    "Rejected",
    "Overtime",
    "Payroll",
  ]);

  data.forEach((employee) => {
    employee.weeks?.forEach((week) => {
      week.children?.forEach((day) => {
        rows.push([
          employee.id ?? "",
          employee.name ?? "",
          employee.username ?? "",
          week.weekNumber ?? "",
          safeDate(day.date),
          formatNum(day.activeTime),
          formatNum(day.idleTime),
          formatNum(day.requested),
          formatNum(day.rejectedTime),
          formatNum(day.overtime),
          formatNum(day.payroll),
        ]);
      });
    });

    // space between users
    rows.push([
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
  });

  return rows;
}

function calculateEmployeeTotal(employee) {
  let total = 0;

  employee.weeks?.forEach((week) => {
    week.children?.forEach((day) => {
      total += Number(day.payroll ?? 0);
    });
  });

  return total;
}

function calculateWeekTotal(week) {
  let total = 0;

  week.children?.forEach((day) => {
    total += Number(day.payroll ?? 0);
  });

  return total;
}

function formatNum(n) {
  if (n === null || n === undefined || n === "") return "";

  const num = Number(n);

  if (isNaN(num)) return String(n);

  return num.toFixed(2);
}

function safeDate(d) {
  if (!d) return "";

  return d.replace(/-/g, "/");
}

/**
 * Generates an XLSX with 2 tabs:
 * - Summary
 * - Daily Details
 */
export function downloadPayrollWorkbook(
  data,
  filename = "Payroll_Report.xlsx"
) {
  const summaryRows = convertHierarchicalToCSV(data);
  const dailyRows = convertDailyDetails(data);

  const workbook = XLSX.utils.book_new();

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  const dailySheet = XLSX.utils.aoa_to_sheet(dailyRows);

  XLSX.utils.book_append_sheet(
    workbook,
    summarySheet,
    "Summary"
  );

  XLSX.utils.book_append_sheet(
    workbook,
    dailySheet,
    "Daily Details"
  );

  XLSX.writeFile(workbook, filename);
}